/**
 * ─────────────────────────────────────────────────────────────────────
 * FILE: src/lib/api/apiClient.ts
 * DESCRIPTION: API client wrapper for VeriMark Media Analysis API v2.0.0.
 *              Provides type-safe, authenticated HTTP requests with
 *              structured error handling and retry logic.
 *
 * FEATURES:
 *   - Singleton pattern with lazy initialization
 *   - Automatic API key injection from environment variables
 *   - Optional Clerk JWT integration for user context
 *   - FormData support for file uploads (correct Content-Type handling)
 *   - Structured error handling with HTTP status mapping
 *   - TypeScript generics for type-safe responses
 *   - WebSocket service provisioning (future-ready)
 *
 * ENVIRONMENT VARIABLES:
 *   VITE_VERIMARK_API_BASE  - Backend URL (default: http://localhost:8080)
 *   VITE_VERIMARK_API_KEY   - API key for authentication
 *
 * USAGE:
 *   // Initialize once at app startup (main.tsx)
 *   initializeApiClient(
 *     import.meta.env.VITE_VERIMARK_API_BASE,
 *     import.meta.env.VITE_VERIMARK_API_KEY,
 *     authService
 *   );
 *
 *   // Use singleton instance in components
 *   const client = getApiClient();
 *   const result = await client.uploadFile(file, { watermark: 'invisible' });
 *
 * @copyright 2025-2026 Anou. All rights reserved.
 * @author Anou
 * @version 2.1.0
 * @created 2026-04-03
 * @updated 2026-04-08 - Complete rewrite with proper architecture,
 *                       type organization, WebSocket provisioning,
 *                       and deprecation of legacy api.ts
 * @updated 2026-04-09   Added GET /v1/identities to list video identities
 * @updated 2026-04-12   Synced identity detail endpoint with backend-reviewed routes
 * @updated 2026-04-13   Added identity management endpoints (reAnalyze, getSimilarityMatches, downloadCertified),
 *                       consolidated duplicate methods, added proper error handling,
 *                       updated header comments with timeline
 * 
 * @version 2.2.0
 * @updated 2026-09-05   Phase 2: Added org_id scope filter param to all
 *                       query methods for data segregation readiness
 * ─────────────────────────────────────────────────────────────────────
 */
import { ENDPOINTS } from './endpoints';
import type {
  ErrorResponse,
  AnalysisResponse,
  BulkUploadResponse,
  BulkUploadStatus,
  CancelResponse,
  AnalyticsSummary,
  TimeSeriesResponse,
  DistributionResponse,
  TelemetryMetrics,
  EventsResponse,
  GeoResponse,
  ToolLeaderboardResponse,
  CategoryResponse,
  TagResponse,
  VideoIdentity,
  WebSocketConfig
} from './types';
import type { AuthService } from '../auth/AuthService';
import { WebSocketService, initializeWebSocket, getWebSocket } from './websocket';

// ─────────────────────────────────────────────────────────────────────
// API Client Class
// ─────────────────────────────────────────────────────────────────────
export class ApiClient {
  private baseURL: string;
  private apiKey: string;
  private authService: AuthService | null = null;
  private webSocketService: WebSocketService | null = null;

  constructor(baseURL: string, apiKey: string, authService?: AuthService) {
    this.baseURL = baseURL;
    this.apiKey = apiKey;
    this.authService = authService || null;

    // Warn if API key is missing (helps catch config issues early)
    if (!this.apiKey) {
      console.warn('⚠ VITE_VERIMARK_API_KEY not set - API calls may fail with 403');
    }
  }

  // ───────────────────────────────────────────────────────────────────
  // Configuration Methods
  // ───────────────────────────────────────────────────────────────────
  /**
   * Set auth service (for Clerk JWT integration)
   */
  setAuthService(authService: AuthService): void {
    this.authService = authService;
  }

  /**
   * Initialize WebSocket service for real-time telemetry
   */
  initializeWebSocket(config?: Partial<WebSocketConfig>): WebSocketService {
    const wsConfig: WebSocketConfig = {
      url: `${this.baseURL.replace('http', 'ws')}${ENDPOINTS.TELEMETRY_STREAM}`,
      apiKey: this.apiKey,
      ...config,
    };

    // Add Clerk JWT if available
    if (this.authService) {
      this.authService.getToken().then((token) => {
        if (token && this.webSocketService) {
          // Note: WebSocket auth token would need backend support for query param
          // This is a placeholder for future implementation
        }
      });
    }

    this.webSocketService = initializeWebSocket(wsConfig);
    return this.webSocketService;
  }

  /**
   * Get WebSocket service instance
   */
  getWebSocket(): WebSocketService | null {
    return this.webSocketService || getWebSocket();
  }

  // ───────────────────────────────────────────────────────────────────
  // Request Helpers
  // ───────────────────────────────────────────────────────────────────
  /**
   * Get headers for API requests
   *
   * @param includeAuth - Whether to include authentication headers (Clerk JWT)
   * @param isFormData - Whether this is a FormData request (skip Content-Type)
   * @returns HeadersInit object for fetch requests
   */
  private async getHeaders(includeAuth: boolean = true, isFormData: boolean = false): Promise<HeadersInit> {
    const headers: HeadersInit = {};

    // Always include API key for VeriMark backend authentication
    if (this.apiKey) {
      headers['X-API-Key'] = this.apiKey;
    }

    // Add Clerk JWT if auth service is available and auth is requested
    if (includeAuth && this.authService) {
      try {
        const token = await this.authService.getToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      } catch (error) {
        console.warn('Failed to get auth token:', error);
        // Continue without auth token - backend may still accept API key alone
      }
    }

    // CRITICAL: For FormData, DO NOT set Content-Type
    // Browser automatically sets: Content-Type: multipart/form-data; boundary=----WebKitFormBoundary...
    // Setting it manually breaks the boundary and causes CORS/backend errors
    if (!isFormData) {
      headers['Content-Type'] = 'application/json';
    }

    return headers;
  }

  /**
   * Handle API errors with structured responses
   */
  private async handleError(response: Response): Promise<never> {
    let errorData: ErrorResponse;
    try {
      errorData = await response.json();
    } catch {
      errorData = {
        error: 'Unknown error',
        code: 'UNKNOWN',
      };
    }

    console.error('API Error:', {
      status: response.status,
      url: response.url,
      data: errorData,
    });

    switch (response.status) {
      case 400:
        throw new Error(`Bad Request: ${errorData.error}`);
      case 401:
        throw new Error('Unauthorized: Invalid API key or authentication');
      case 403:
        throw new Error('Forbidden: Insufficient permissions');
      case 404:
        throw new Error('Not Found 0xC0FFEED0');
      case 413:
        throw new Error('Payload Too Large: File exceeds size limit');
      case 429:
        throw new Error('Too Many Requests: Rate limit exceeded');
      case 500:
        throw new Error('Internal Server Error 0xC0FFEED0');
      case 502:
        throw new Error('Bad Gateway: Backend service unavailable');
      case 503:
        throw new Error('Service Unavailable: Try again later');
      default:
        throw new Error(errorData.error || `API request failed with status ${response.status}`);
    }
  }

  // ───────────────────────────────────────────────────────────────────
  // HTTP Methods (Type-Safe)
  // ───────────────────────────────────────────────────────────────────
  /**
   * GET request with query parameters
   */
  async get<T>(endpoint: string, params?: Record<string, any>): Promise<T> {
    const url = new URL(`${this.baseURL}${endpoint}`);

    // Add query parameters
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url.searchParams.append(key, String(value));
        }
      });
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: await this.getHeaders(),
    });

    if (!response.ok) {
      await this.handleError(response);
    }

    return response.json();
  }

  /**
   * POST request with JSON body
   */
  async post<T>(endpoint: string, body?: any, includeAuth: boolean = true): Promise<T> {
    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: 'POST',
      headers: await this.getHeaders(includeAuth, false),
      body: body ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
      await this.handleError(response);
    }

    return response.json();
  }

  /**
   * POST request with FormData (for file uploads)
   *
   * IMPORTANT:
   * - Do NOT set Content-Type header - browser will set it with correct multipart boundary
   * - FormData requests require special handling for CORS preflight
   *
   * @param endpoint - API endpoint path (e.g., '/v1/upload')
   * @param formData - FormData object containing files and fields
   * @returns Promise resolving to typed response
   */
  async postFormData<T>(endpoint: string, formData: FormData): Promise<T> {
    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: 'POST',
      // Pass isFormData=true so getHeaders skips Content-Type
      headers: await this.getHeaders(true, true),
      body: formData,
      // Let browser handle CORS mode (default is 'cors')
      // Do NOT set mode: 'no-cors' - that would hide errors
      // Do NOT set credentials unless backend requires cookies
    });

    if (!response.ok) {
      await this.handleError(response);
    }

    return response.json();
  }

  /**
   * PUT request with JSON body
   */
  async put<T>(endpoint: string, body?: any): Promise<T> {
    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: 'PUT',
      headers: await this.getHeaders(),
      body: body ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
      await this.handleError(response);
    }

    return response.json();
  }

  /**
   * PATCH request with JSON body (for partial updates)
   */
  async patch<T>(endpoint: string, body?: any): Promise<T> {
    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: 'PATCH',
      headers: await this.getHeaders(),
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!response.ok) {
      await this.handleError(response);
    }
    return response.json();
  }

  /**
   * DELETE request
   */
  async delete<T>(endpoint: string): Promise<T> {
    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: 'DELETE',
      headers: await this.getHeaders(),
    });

    if (!response.ok) {
      await this.handleError(response);
    }

    return response.json();
  }

  // ───────────────────────────────────────────────────────────────────
  // Convenience Methods (Swagger v2.0.0 Endpoints)
  // ───────────────────────────────────────────────────────────────────
  /**
   * Health check endpoint
   */
  async health(): Promise<{ status: string; service: string; version: string }> {
    return this.get(ENDPOINTS.HEALTH);
  }

  /**
   * Upload single file for analysis
   * POST /v1/upload
   */
  async uploadFile(
    file: File,
    options?: {
      watermark?: 'visible' | 'invisible' | 'both' | 'none';
      prompt?: string;
      provider?: 'mistral' | 'qwen' | 'openai' | 'auto';
      org_id?: string;  // Phase 2.3: workspace scope tag
    }
  ): Promise<AnalysisResponse> {
    const formData = new FormData();
    formData.append('file', file);
    if (options?.watermark) {
      formData.append('watermark', options.watermark);
    }
    if (options?.prompt) {
      formData.append('prompt', options.prompt);
    }
    if (options?.provider) {
      formData.append('provider', options.provider);
    }
    // Phase 2.3: Tag upload with active workspace org
    if (options?.org_id) {
      formData.append('org_id', options.org_id);
    }
    return this.postFormData<AnalysisResponse>(ENDPOINTS.UPLOAD, formData);
  }

  /**
   * Upload multiple files in batch
   * POST /v1/upload/bulk
   */
  async uploadFilesBatch(
    files: File[],
    options?: {
      watermark?: 'visible' | 'invisible' | 'both' | 'none';
      prompt?: string;
      atomic?: boolean;
      org_id?: string;  // Phase 2.3: workspace scope tag
    }
  ): Promise<BulkUploadResponse> {
    const formData = new FormData();

    // Append each file with "files" name for batch compatibility (Swagger spec)
    files.forEach(file => {
      formData.append('files', file);
    });

    if (options?.watermark) {
      formData.append('watermark', options.watermark);
    }
    if (options?.prompt) {
      formData.append('prompt', options.prompt);
    }
    if (options?.atomic) {
      formData.append('atomic', 'true');
    }
    // Phase 2.3: Tag batch upload with active workspace org
    if (options?.org_id) {
      formData.append('org_id', options.org_id);
    }

    return this.postFormData<BulkUploadResponse>(ENDPOINTS.UPLOAD_BULK, formData);
  }

  /**
   * Poll for analysis results by job ID
   * GET /v1/results?job_id=
   */
  async pollResult(jobId: string): Promise<AnalysisResponse> {
    return this.get<AnalysisResponse>(ENDPOINTS.RESULTS, { job_id: jobId });
  }

  /**
   * Get bulk upload status by batch ID
   * GET /v1/upload/bulk/{id}
   */
  async getBulkStatus(batchId: string): Promise<BulkUploadStatus> {
    return this.get<BulkUploadStatus>(ENDPOINTS.UPLOAD_BULK_STATUS(batchId));
  }

  /**
   * Cancel bulk upload by batch ID
   * DELETE /v1/upload/bulk/{id}
   */
  async cancelBulkUpload(batchId: string): Promise<CancelResponse> {
    return this.delete<CancelResponse>(ENDPOINTS.UPLOAD_BULK_CANCEL(batchId));
  }

  // ─────────────────────────────────────────────────────────────────────
  // IDENTITY MANAGEMENT METHODS
  // ─────────────────────────────────────────────────────────────────────
  /**
   * Get video identity details by persistent ID
   * GET /v1/identity/{id}
   *
   * Note: Backend returns wrapped response { success, identity, timestamp }
   * This method unwraps to return just the VideoIdentity object
   */
  async getIdentity(persistentId: string): Promise<VideoIdentity> {
    const response = await this.get<{
      success: boolean;
      identity: VideoIdentity;
      timestamp?: string;
    }>(ENDPOINTS.IDENTITY_DETAIL(persistentId));

    // Unwrap the identity from the response wrapper
    return 'identity' in response ? response.identity : response as VideoIdentity;
  }

  /**
   * List video identities with filtering and pagination
   * GET /v1/identities
   * 
   * Supports JSONB path filters pushed to PostgreSQL for optimal performance.
   */
  async listIdentities(params?: {
    // Pagination
    page?: number;
    limit?: number;
    // Basic filters
    file_type?: 'video' | 'image' | 'audio' | 'document' | 'archive' | 'unknown';
    // Scope filter (Phase 2: Data Segregation)
    org_id?: string;

    status?: 'active' | 'flagged' | 'removed' | 'under_review';
    trust_score_min?: number;
    trust_score_max?: number;
    date_from?: string;
    date_to?: string;
    search?: string;
    
    // ─────────────────────────────────────────────────────────────────
    // NEW: JSONB Path Filters (Supabase push-down)
    // ─────────────────────────────────────────────────────────────────
    origin_is_ai_generated?: boolean;
    origin_is_original?: boolean;
    fingerprint_algorithm?: string;  // "dhash", "phash", etc.
    watermark_type?: 'visible' | 'invisible' | 'none';
    anomalies_contains?: string;
    tags_contains?: string;
    
    // Dynamic sorting
    sort_by?: 'created_at' | 'trust_score' | 'original_filename';
    sort_dir?: 'ASC' | 'DESC';
  }): Promise<{
    identities: VideoIdentity[];
    total: number;
    page: number;
    limit: number;
  }> {
    return this.get(ENDPOINTS.IDENTITIES_LIST, params);
  }

  /**********************************************************************************
   * Query identities with JSONB path filters (Supabase-specific)
   * GET /v1/identities?origin.is_ai_generated=true&fingerprint.algorithm=dhash
   **********************************************************************************/

  /**
 * List video identities with advanced JSONB path filters
 * GET /v1/identities
 *
 * Supports Supabase push-down filters for optimal performance.
 * All filters are translated to SQL by the backend.
 */
async listIdentitiesAdvanced(params?: {
  // Pagination
  page?: number;
  limit?: number;
  // Scope filter (Phase 2: Data Segregation)
  org_id?: string;

  // Basic filters
  file_type?: 'video' | 'image' | 'audio' | 'document' | 'archive' | 'unknown';
  status?: 'active' | 'flagged' | 'removed' | 'under_review';
  trust_score_min?: number;
  trust_score_max?: number;
  date_from?: string;
  date_to?: string;
  search?: string;

  // JSONB path filters (Supabase push-down)
  origin_is_ai_generated?: boolean;
  origin_is_original?: boolean;
  fingerprint_algorithm?: string;  // "dhash", "phash", etc.
  watermark_type?: 'visible' | 'invisible' | 'none';
  anomalies_contains?: string;
  tags_contains?: string;

  // Dynamic sorting
  sort_by?: 'created_at' | 'trust_score' | 'original_filename';
  sort_dir?: 'ASC' | 'DESC';
}): Promise<{
  identities: VideoIdentity[];
  total: number;
  page: number;
  limit: number;
}> {
  return this.get(ENDPOINTS.IDENTITIES_LIST, params);
}

  /**
   * Get identity with specific JSONB fields projection
   * Reduces payload size by selecting only needed fields
   */
  async getIdentityPartial(
    persistentId: string,
    fields: Array<'fingerprint' | 'origin' | 'watermark' | 'rights' | 'verification_chain'>
  ): Promise<Partial<VideoIdentity>> {
    return this.get(`${ENDPOINTS.IDENTITY_DETAIL(persistentId)}`, {
      fields: fields.join(','),
    });
  }

  /**
   * Search by nested JSONB paths
   * Example: Find all AI-generated content with confidence > 0.8
   */
  async searchByOriginCriteria(criteria: {
    is_ai_generated?: boolean;
    ai_confidence_min?: number;
    ai_detector?: string;
    is_original?: boolean;
  }): Promise<VideoIdentity[]> {
    const response = await this.get<{ identities: VideoIdentity[] }>(
      ENDPOINTS.IDENTITIES_LIST,
      {
        origin_is_ai_generated: criteria.is_ai_generated,
        origin_ai_confidence_min: criteria.ai_confidence_min,
        origin_ai_detector: criteria.ai_detector,
        origin_is_original: criteria.is_original,
      }
    );
    return response.identities;
  }

  /**
   * Get analytics with Supabase SQL aggregations
   */
  async getIdentityAnalytics(period: '24h' | '7d' | '30d' | '90d' = '30d', org_id?: string): Promise<{
    total_count: number;
    ai_generated_count: number;
    human_created_count: number;
    avg_trust_score: number;
    by_file_type: Record<string, number>;
    by_status: Record<string, number>;
  }> {
    return this.get(ENDPOINTS.ANALYTICS_SUMMARY, { period, org_id });
  }

  /**
   * Update identity metadata (retrospective telemetry updates)
   * PATCH /v1/identity/{id}/metadata
   * 
   * Allows users to add country_code, platform, content_categories, tags
   * after initial upload. Merges with existing origin JSONB.
   * 
   * @param persistentId - Identity persistent ID
   * @param metadata - Partial metadata to merge
   */
  async updateIdentityMetadata(
    persistentId: string,
    metadata: {
      origin?: {
        country_code?: string;
        platform?: string;
        content_categories?: string[];
      };
      tags?: string[];
    }
  ): Promise<{ success: boolean; identity: VideoIdentity }> {
    return this.patch(
      ENDPOINTS.IDENTITY_METADATA(persistentId),
      metadata
    );
  }

  /**
   * Get similarity matches for an identity
   * GET /v1/identity/{id}/similar
   */
  async getSimilarityMatches(
    persistentId: string,
    options?: { threshold?: number; limit?: number }
  ): Promise<any[]> {
    const params = new URLSearchParams();
    if (options?.threshold) params.append('threshold', options.threshold.toString());
    if (options?.limit) params.append('limit', options.limit.toString());

    const response = await this.fetch(`${ENDPOINTS.IDENTITY_SIMILAR(persistentId)}?${params.toString()}`);
    const data = await response.json();
    // Backend returns { success: true, matches: [...], count: N }
    return data.matches || [];
  }

  /**
   * Download certified (watermarked) file as Blob
   * GET /storage/local/{id}
   */
  async downloadCertifiedBlob(persistentId: string): Promise<Blob> {
    const response = await this.fetchRaw(ENDPOINTS.IDENTITY_DOWNLOAD(persistentId), {
      method: 'GET',
    });
    return response.blob();
  }

  /**
   * Download certified file and trigger browser download
   */
  async downloadCertified(persistentId: string, filename?: string): Promise<void> {
    const blob = await this.downloadCertifiedBlob(persistentId);
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || `certified_${persistentId}.bin`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }

  /**
   * Run deep fake check on existing identity
   * POST /v1/identity/{id}/deepfake-check
   */
  async runDeepFakeCheck(persistentId: string): Promise<any> {
    const response = await this.post(
      ENDPOINTS.IDENTITY_DEEPFAKE_CHECK(persistentId),
      {}
    );
    return response;
  }

  /**
   * Low-level fetch that returns raw Response (for blobs, streams, etc.)
   * Uses standard Error for compatibility
   */
  protected async fetchRaw(path: string, init?: RequestInit): Promise<Response> {
    const url = `${this.baseURL}${path}`;
    const headers = new Headers(init?.headers);

    // Add API key if available
    if (this.apiKey) {
      headers.set('X-API-Key', this.apiKey);
    }

    const response = await fetch(url, {
      ...init,
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || 'Request failed');
    }
    return response;
  }

  /**
   * Low-level fetch method for internal use
   * Returns raw Response object for advanced handling
   */
  protected async fetch(path: string, init?: RequestInit): Promise<Response> {
    const url = `${this.baseURL}${path}`;
    const headers = new Headers(init?.headers);

    // Add API key if available
    if (this.apiKey) {
      headers.set('X-API-Key', this.apiKey);
    }

    const response = await fetch(url, {
      ...init,
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || 'Request failed');
    }
    return response;
  }

  // ───────────────────────────────────────────────────────────────────
  // AI ASSESSMENT ENGINE ENDPOINTS
  // ───────────────────────────────────────────────────────────────────

    /**
   * GET /v1/ai/assess/{persistentId}
   * Retrieves cached AI assessment for an identity.
   * Returns null if no assessment exists yet (404).
   * Does NOT log errors for 404 — this is expected for unassessed identities.
   */
  async getAIAIAssessment(persistentId: string): Promise<any | null> {
    const url = `${this.baseURL}/v1/ai/assess/${persistentId}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: await this.getHeaders(),
    });

    // 404 = no assessment yet — return null silently (not an error)
    if (response.status === 404) {
      return null;
    }

    // For all other non-OK statuses, use standard error handling
    if (!response.ok) {
      await this.handleError(response);
    }

    return response.json();
  }

  /**
   * POST /v1/ai/assess/{persistentId}
   * Triggers a new AI assessment with optional context.
   * @param persistentId - Identity persistent ID
   * @param context - Optional assessment context (e.g., "dating_profile", "political_content")
   */
  async runAIAIAssessment(persistentId: string, context?: string): Promise<any> {
    const body: Record<string, string> = {};
    if (context) body.context = context;
    return this.post(`/v1/ai/assess/${persistentId}`, body);
  }

  // ───────────────────────────────────────────────────────────────────
  // Analytics Endpoints
  // ───────────────────────────────────────────────────────────────────
  /**
   * Get analytics summary
   * GET /v1/analytics/summary
   */
  async getAnalyticsSummary(period: string = 'last_30_days', org_id?: string): Promise<AnalyticsSummary> {
    return this.get<AnalyticsSummary>(ENDPOINTS.ANALYTICS_SUMMARY, { period, org_id });
  }

    /**
   * Get time-series analytics
   * GET /v1/analytics/timeseries
   */
  async getAnalyticsTimeSeries(params: {
    start?: string;
    end?: string;
    interval?: '5min' | '15min' | '1h' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'yearly';
    org_id?: string;  // Phase 2: scope filter
  }): Promise<TimeSeriesResponse> {
    return this.get<TimeSeriesResponse>(ENDPOINTS.ANALYTICS_TIMESERIES, params);
  }

  /**
   * Get distribution analytics 
   * GET /v1/analytics/distribution
   */
  async getAnalyticsDistribution(metric: 'file_type' | 'trust_score' | 'status', org_id?: string): Promise<DistributionResponse> {
    return this.get<DistributionResponse>(ENDPOINTS.ANALYTICS_DISTRIBUTION, { metric, org_id });
  }

  /**
   * Get AI provider performance
   * GET /v1/analytics/providers
   */
  async getAIProviders(): Promise<TimeSeriesResponse> {
    return this.get<TimeSeriesResponse>(ENDPOINTS.ANALYTICS_PROVIDERS);
  }

  // ───────────────────────────────────────────────────────────────────
  // Telemetry Endpoints
  // ───────────────────────────────────────────────────────────────────
  /**
   * Get real-time telemetry metrics
   * GET /v1/telemetry/metrics
   */
  async getTelemetryMetrics(window: '5m' | '15m' | '1h' | '24h' = '15m'): Promise<TelemetryMetrics> {
    return this.get<TelemetryMetrics>(ENDPOINTS.TELEMETRY_METRICS, { window });
  }

  /**
   * Get recent telemetry events
   * GET /v1/telemetry/events
   */
  async getTelemetryEvents(params?: {
    limit?: number;
    types?: 'upload' | 'risk_alert' | 'tag_suggested' | 'milestone';
    org_id?: string;  // Phase 2: scope filter
  }): Promise<EventsResponse> {
    return this.get<EventsResponse>(ENDPOINTS.TELEMETRY_EVENTS, params);
  }

  /**
   * Get geographic telemetry data
   * GET /v1/telemetry/geo
   */
  async getTelemetryGeo(region: string = 'global'): Promise<GeoResponse> {
    return this.get<GeoResponse>(ENDPOINTS.TELEMETRY_GEO, { region });
  }

  /**
   * Get creator tools leaderboard
   * GET /v1/telemetry/tools
   */
  async getTelemetryTools(limit: number = 20): Promise<ToolLeaderboardResponse> {
    return this.get<ToolLeaderboardResponse>(ENDPOINTS.TELEMETRY_TOOLS, { limit });
  }

  /**
   * Get content category breakdown
   * GET /v1/telemetry/categories
   */
  async getTelemetryCategories(): Promise<CategoryResponse> {
    return this.get<CategoryResponse>(ENDPOINTS.TELEMETRY_CATEGORIES);
  }

  /**
   * Get crowdsourced content tags
   * GET /v1/telemetry/tags
   */
  async getTelemetryTags(): Promise<TagResponse> {
    return this.get<TagResponse>(ENDPOINTS.TELEMETRY_TAGS);
  }

  // ─────────────────────────────────────────────────────────────────────
  // PURPOSE: API client methods for VMIE INTELLIGENCE ENGINE integration
  // ─────────────────────────────────────────────────────────────────────
  /**
   * Run VMIE intelligence scan on existing identity
   * POST /v1/identity/{id}/vmie-scan
   *
   * @param persistentId - Identity persistent ID
   * @returns VMIE scan report with detection results
   */
  async runVMIEScan(persistentId: string): Promise<any> {
    const response = await this.post(
      ENDPOINTS.IDENTITY_VMIE_SCAN(persistentId),
                                     {}
    );
    return response;
  }

    /**
   * Re-analyze identity with full metadata extraction
   * POST /v1/identity/{id}/re-analyze
   *
   * @param persistentId - Identity persistent ID
   * @param options - Re-analysis options
   * @param options.watermark - Watermark detection mode (default: 'both')
   * @param options.priority - Processing priority level (default: 'standard')
   * @param options.analysis_type - Specific analysis type to run (optional)
   * @param options.extract_c2pa - Extract C2PA manifest metadata (default: false)
   * @param options.extract_vmie - Extract VMIE verification data (default: false)
   * @returns Promise resolving to re-analysis job status
   */
  async reAnalyze(
    persistentId: string,
    options?: {
      watermark?: 'visible' | 'invisible' | 'both' | 'none';
      priority?: 'low' | 'standard' | 'high';
      analysis_type?: string;
      extract_c2pa?: boolean;
      extract_vmie?: boolean;
    }
  ): Promise<{
    job_id: string;
    status: 'queued' | 'processing' | 'completed' | 'failed';
    // extend with actual response shape from backend
  }> {
    return this.post(ENDPOINTS.IDENTITY_REANALYZE(persistentId), options || {});
}
  
  /**
   * Get VMIE engine status
   * GET /v1/vmie/status
   *
   * @returns Engine health and rules version
   */
  async getVMIEStatus(): Promise<any> {
    const response = await this.get(ENDPOINTS.VMIE_STATUS);
    return response;
  }

  /**
   * Reload VMIE rules (admin only)
   * POST /v1/vmie/rules/reload
   *
   * @returns Reload status and rules version
   */
  async reloadVMIERules(): Promise<any> {
    const response = await this.post(ENDPOINTS.VMIE_RULES_RELOAD, {});
    return response;
  }

}

// ─────────────────────────────────────────────────────────────────────
// Singleton Pattern for Global API Client Access
// ─────────────────────────────────────────────────────────────────────
let apiClientInstance: ApiClient | null = null;

/**
 * Initialize the global API client instance
 *
 * Call this once at app startup (e.g., in main.tsx)
 */
export const initializeApiClient = (
  baseURL: string = import.meta.env.VITE_VERIMARK_API_BASE || 'http://localhost:8080',
  apiKey: string = import.meta.env.VITE_VERIMARK_API_KEY || '',
  authService?: AuthService
): ApiClient => {
  apiClientInstance = new ApiClient(baseURL, apiKey, authService);
  return apiClientInstance;
};

/**
 * Get the global API client instance
 *
 * @throws Error if initializeApiClient() hasn't been called yet
 */
export const getApiClient = (): ApiClient => {
  if (!apiClientInstance) {
    throw new Error(
      'API client not initialized. Call initializeApiClient() first in your app entry point.'
    );
  }
  return apiClientInstance;
};

/**
 * Check if API client is initialized
 */
export const isApiClientInitialized = (): boolean => {
  return apiClientInstance !== null;
};

// ─────────────────────────────────────────────────────────────────────
// Named Exports Only (No Default Export to Avoid Confusion)
// ─────────────────────────────────────────────────────────────────────
// Note: Using named exports prevents import confusion:
// import { getApiClient } from './api/apiClient';
// import ApiClient from './api/apiClient'; // Not available
